/** Weekly Express bands: days 1–maxDays inclusive → interest % */
export type ExpressRateBand = {
  maxDays: number;
  rate: number;
};

/** Default: +15% per week through 30 days (Express max tenor). */
export const DEFAULT_EXPRESS_RATE_SCHEDULE: ExpressRateBand[] = [
  { maxDays: 7, rate: 15 },
  { maxDays: 14, rate: 30 },
  { maxDays: 21, rate: 45 },
  { maxDays: 30, rate: 60 },
];

export function sortExpressBands(bands: ExpressRateBand[]): ExpressRateBand[] {
  return [...bands].sort((a, b) => a.maxDays - b.maxDays);
}

export function parseExpressRateSchedule(raw: unknown): ExpressRateBand[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const bands: ExpressRateBand[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const maxDays = Number(r.maxDays ?? r.max_days);
    const rate = Number(r.rate);
    if (!Number.isFinite(maxDays) || maxDays < 1 || !Number.isFinite(rate) || rate < 0) continue;
    bands.push({ maxDays: Math.round(maxDays), rate });
  }
  return bands.length ? sortExpressBands(bands) : null;
}

export function expressRateForDays(schedule: ExpressRateBand[], days: number): number {
  const d = Math.max(1, Math.round(days));
  const sorted = sortExpressBands(schedule);
  for (const band of sorted) {
    if (d <= band.maxDays) return band.rate;
  }
  return sorted[sorted.length - 1]?.rate ?? DEFAULT_EXPRESS_RATE_SCHEDULE[0].rate;
}

export function expressRepayAmount(principal: number, schedule: ExpressRateBand[], days: number): number {
  const rate = expressRateForDays(schedule, days);
  return principal * (1 + rate / 100);
}

export function formatExpressBandLabel(band: ExpressRateBand, prevMax: number): string {
  const min = prevMax + 1;
  if (min === 1 && band.maxDays === 1) return `1 day · ${band.rate}%`;
  if (min === 1) return `1–${band.maxDays} days · ${band.rate}%`;
  return `${min}–${band.maxDays} days · ${band.rate}%`;
}

export function describeExpressSchedule(schedule: ExpressRateBand[]): string[] {
  const sorted = sortExpressBands(schedule);
  let prev = 0;
  return sorted.map((b) => {
    const line = formatExpressBandLabel(b, prev);
    prev = b.maxDays;
    return line;
  });
}

export function impliedRatePercent(principal: number, repay: number): number {
  if (principal <= 0) return 0;
  return Number((((repay - principal) / principal) * 100).toFixed(2));
}
